import axios from 'axios';
import { API_ENDPOINTS } from '../api-config';
import { fetchAuthSession } from 'aws-amplify/auth';

// Define TypeScript interfaces for API data
export interface UserProfile {
  username: string;
  email: string;
  name: string;
  subscription: string;
  presentationsUsed: number;
  joinDate: string;
}

export interface Presentation {
  userId: string;
  uploadId: string;
  uploadDate: string;
  status: 'pending' | 'processing' | 'completed' | 'error';
  s3UploadedDocuments: string[];
  s3CompletedPresentation: string;
  textContentUrl?: string;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  errorMessage?: string;
  uploadMetadata?: {
    filesCount: number;
    hasTextContent: boolean;
    originalFileNames: string[];
  };
}

export interface SubscriptionPlan {
  id: string;
  name: string;
  presentations: number | 'Unlimited';
  monthlyPrice: number;
  annualPrice: number;
  features: string[];
}

// Create axios instance with interceptors for auth
const apiClient = axios.create();

// Add request interceptor to add authentication token
apiClient.interceptors.request.use(async (config) => {
  try {
    console.log('API request interceptor called for:', config.method?.toUpperCase(), config.url);
    const { tokens } = await fetchAuthSession();
    const token = tokens?.idToken?.toString();
    console.log('Auth session fetched, token available:', !!token);
    if (config.headers && token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('Authorization header set, token length:', token.length);
    } else {
      console.log('No token available or no headers object');
    }
  } catch (error) {
    console.error('Error adding auth token to request:', error);
  }
  return config;
});

// User API
export const userAPI = {
  getProfile: () => apiClient.get<UserProfile>(API_ENDPOINTS.USER.PROFILE),
  updateProfile: (data: Partial<UserProfile>) => apiClient.put<UserProfile>(API_ENDPOINTS.USER.UPDATE, data),
  deleteAccount: () => apiClient.delete(API_ENDPOINTS.USER.DELETE),
};

// Subscription API
export const subscriptionAPI = {
  getPlans: () => apiClient.get<SubscriptionPlan[]>(API_ENDPOINTS.SUBSCRIPTIONS.PLANS),
  createCheckoutSession: (planId: string, billingCycle: string) => 
    apiClient.post(API_ENDPOINTS.SUBSCRIPTIONS.CREATE_CHECKOUT_SESSION, { planId, billingCycle }),
  subscribe: (planId: string, billingCycle: string) => 
    apiClient.post(API_ENDPOINTS.SUBSCRIPTIONS.SUBSCRIBE, { planId, billingCycle }),
  changePlan: (planId: string, billingCycle: string) => 
    apiClient.put(API_ENDPOINTS.SUBSCRIPTIONS.CHANGE_PLAN, { planId, billingCycle }),
  cancelSubscription: () => apiClient.post(API_ENDPOINTS.SUBSCRIPTIONS.CANCEL),
  getUsage: () => apiClient.get(API_ENDPOINTS.SUBSCRIPTIONS.USAGE),
};

// Presentations API
export const presentationsAPI = {
  getAll: () => apiClient.get<Presentation[]>(API_ENDPOINTS.PRESENTATIONS.LIST),
  create: (data: FormData) => apiClient.post<Presentation>(API_ENDPOINTS.PRESENTATIONS.UPLOAD, data, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  getById: (id: string) => apiClient.get<Presentation>(API_ENDPOINTS.PRESENTATIONS.DETAILS(id)),
  update: (id: string, data: Partial<Presentation>) => 
    apiClient.put<Presentation>(API_ENDPOINTS.PRESENTATIONS.UPDATE(id), data),
  delete: (id: string) => apiClient.delete(API_ENDPOINTS.PRESENTATIONS.DELETE(id)),
  download: (id: string) => apiClient.get<{downloadUrl: string; filename: string}>(API_ENDPOINTS.PRESENTATIONS.DOWNLOAD(id)),
  export: (id: string, format: string) => 
    apiClient.post(API_ENDPOINTS.PRESENTATIONS.EXPORT(id), { format }, { responseType: 'blob' }),
};

// Upload API
export const uploadAPI = {
  uploadFile: (file: File, onProgress?: (progress: number) => void) => {
    const formData = new FormData();
    formData.append('file', file);
    
    return apiClient.post(API_ENDPOINTS.UPLOAD, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      },
    });
  },
};

// Notification API for real-time updates
export const notificationAPI = {
  connectSSE: async (onMessage: (data: any) => void, onError?: (error: Event) => void) => {
    try {
      // Get auth token
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      if (!token) {
        throw new Error('No authentication token available');
      }

      // EventSource doesn't support custom headers, so we'll pass the token as a query parameter
      const sseUrl = `${API_ENDPOINTS.NOTIFICATIONS.SSE}?token=${encodeURIComponent(token)}`;
      console.log('Connecting to SSE:', sseUrl.replace(/token=[^&]+/, 'token=***'));
      
      const eventSource = new EventSource(sseUrl);

      eventSource.onopen = () => {
        console.log('SSE connection opened successfully');
      };

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('SSE message received:', data);
          onMessage(data);
        } catch (error) {
          console.error('Error parsing SSE message:', error, event.data);
        }
      };

      eventSource.onerror = (error) => {
        console.error('SSE connection error:', error);
        console.log('EventSource readyState:', eventSource.readyState);
        
        // Don't automatically reconnect - let the parent component handle it
        if (eventSource.readyState === EventSource.CLOSED) {
          console.log('SSE connection closed by server');
        }
        
        if (onError) {
          onError(error);
        }
      };

      return eventSource;
    } catch (error) {
      console.error('Error establishing SSE connection:', error);
      throw error;
    }
  },

  getStats: () => apiClient.get(API_ENDPOINTS.NOTIFICATIONS.STATS),
  
  sendTestNotification: (userId: string, message: string) => 
    apiClient.post(API_ENDPOINTS.NOTIFICATIONS.TEST, { userId, message })
};